import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { useWalletContext } from '../../Wallet.context';
import {
  asWalletCurrency,
  formatWalletUnitAmount,
  resolveWalletCurrency,
} from '../../Wallet.money';
import { templateTypeToWalletCategory } from '../../hooks/useWalletSendGate';
import { useTemplateCostByCategory } from '../../hooks/useSendCostEstimate';
import { useWalletFlag } from '../../hooks/useWalletFlag';

/**
 * Costo por plantilla para el selector del chat 1 a 1 (RET-1694, pantalla 10).
 *
 * Es la fachada que consume el chat: recibe los `template_type` que hay en
 * pantalla y el teléfono del lead, y devuelve una función que da el texto ya
 * formateado para cada renglón. Así el módulo de chat no toca el endpoint, ni el
 * catálogo de categorías, ni el formateo de dinero — sólo pinta lo que le da
 * esta función.
 *
 * Cotiza **una vez por categoría distinta**, no una por plantilla: con un solo
 * destinatario el precio depende de categoría × país y el país es fijo, así que
 * treinta plantillas de dos categorías son dos llamadas.
 *
 * Devuelve `undefined` para un renglón cuando no hay número que afirmar: wallet
 * apagada, `template_type` que no sabemos traducir al catálogo, o la cotización
 * de esa categoría falló. El renglón entonces conserva su texto de siempre.
 *
 * RET-2106, D-2: el string que devuelve es SOLO el monto ya formateado
 * (`formatWalletUnitAmount`, DS-25), sin el prefijo `"Con costo · "` que
 * llevaba antes (`templateCost.withCost`: la llave se queda en el catálogo de
 * i18n en este deploy para las pestañas que siguen con el bundle viejo — D-3 —
 * pero ningún código de `src/` la lee ya). "Gratis" / "Con costo" ya no son un
 * prefijo delante del monto: son el texto alterno de la columna de precio que pinta
 * `Template.component.tsx` (D-4) cuando esta fachada no tiene un número que
 * afirmar. Esta fachada solo dice CUÁNTO, nunca CÓMO se lee en la fila.
 *
 * D-2b: nunca deja escapar lo que lance `formatWalletUnitAmount` (total
 * negativo, mal formado, fuera del entero seguro) — lo atrapa y devuelve
 * `undefined`, exactamente como cualquier otra cotización que no se pudo
 * afirmar.
 *
 * D-2c: un total cotizado en cero tampoco se afirma (DS-25): devuelve
 * `undefined` y el renglón dice "Con costo".
 */
export function useTemplateCosts(params: {
  templateTypes: readonly (string | undefined)[];
  recipientHandle: string | undefined;
}): (templateType: string | undefined) => string | undefined {
  const { i18n } = useTranslation('wallet');
  const { isWalletEnabled } = useWalletFlag();
  const { balance } = useWalletContext();
  const { subscription } = useSubscriptionContext();

  // Sólo dígitos: los handles ya vienen en E.164 sin `+`, esto quita el ruido
  // de presentación (espacios, guiones, paréntesis) que se cuela al pegar.
  const recipient = String(params.recipientHandle ?? '').replace(/[^\d]/g, '');

  // Las categorías se derivan de lo que hay en pantalla, deduplicadas: es lo que
  // convierte N renglones en ≤4 llamadas.
  const categories = useMemo(() => {
    const found = new Set<string>();

    for (const templateType of params.templateTypes) {
      if (!templateType) continue;

      found.add(templateTypeToWalletCategory(templateType));
    }

    return Array.from(found);
    // La lista de tipos se compara por contenido: el consumidor la construye con
    // .map() en cada render y filtrarla la reordena, así que una dependencia por
    // referencia re-cotizaría en cada filtro.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.templateTypes.join(',')]);

  const { costByCategory } = useTemplateCostByCategory({
    recipient,
    categories,
    enabled: isWalletEnabled && recipient.length > 0,
  });

  return function formattedCostFor(templateType: string | undefined) {
    if (!isWalletEnabled) return undefined;

    if (!templateType) return undefined;

    const category = templateTypeToWalletCategory(templateType);

    const estimate = costByCategory[category];
    if (!estimate) return undefined;

    const currency = resolveWalletCurrency(
      asWalletCurrency(estimate.currency) || balance?.currency || '',
      subscription?.currency,
    );
    if (!currency) return undefined;

    // Un destinatario que el estimador no pudo resolver a un país NO se pinta
    // como gratis ni como cero: no hay tarifa que mostrar, así que el renglón
    // vuelve a su texto de siempre.
    if (estimate.recipientsQuoted === 0) return undefined;

    // DS-25 (Fanny, 29-sep): un cero COTIZADO tampoco se pinta. Este renglón no
    // es gratis en origen —si lo fuera, la fila diría "Gratis" sin preguntar—,
    // así que "$0.0000" al lado de enviar afirmaría una gratuidad que nadie
    // puede respaldar. Cae a "Con costo", como una cotización sin resolver. Se
    // compara el texto y no un BigInt porque un total mal formado todavía no
    // pasó por el guardia del formateador. El signo opcional es a propósito:
    // la frontera del servicio acepta `-0`, y BigInt('-0') es 0n, así que el
    // guardia de negativos del formateador tampoco lo frenaría.
    if (/^-?0+$/.test(estimate.total.amountMicros)) return undefined;

    // RET-2106, D-2: solo el monto, sin prefijo. Formateo UNITARIO
    // (DS-25, cuatro decimales, hacia arriba) y no el de saldo: a dos
    // decimales una tarifa de US$0.0085 se pinta "$0.01" y una más barata
    // "$0.00" — o sea "gratis", justo al lado del botón de enviar. Ver
    // formatWalletUnitAmount.
    //
    // D-2b: esta función corre en el CUERPO del render. Un total negativo,
    // mal formado o fuera del entero seguro hace que formatWalletUnitAmount
    // lance — y una excepción aquí tumbaría el chat entero hasta su
    // ErrorBoundary por un solo renglón de precio. Se atrapa y se devuelve
    // undefined: el mismo "no hay número que afirmar" que ya cubre a
    // cualquier otra cotización sin resolver.
    try {
      return formatWalletUnitAmount({
        amountMicros: estimate.total.amountMicros,
        currency,
        locale: i18n.language,
      });
    } catch {
      return undefined;
    }
  };
}
